/**
 * Ask AttorneyShare: the contract between the assistant's server route
 * (`api/assistant/chat.ts`) and the UI that renders its answers.
 *
 * Every action the assistant can take has a stable name here, and every
 * result it streams back has a typed shape, so the UI renders cards from
 * data rather than from model prose.
 *
 * Kept free of imports: the BFF under `api/` imports this file by relative
 * path (`.js` specifier) and cannot resolve the `@/` alias.
 */

/** The BFF route the chat transport posts to. */
export const ASSISTANT_CHAT_PATH = '/api/assistant/chat'

/** Actions that only read. They run without asking. */
export const ASSISTANT_READ_TOOL_NAMES = [
  'search_marketplace',
  'list_my_cases',
  'get_case',
  'get_needs_attention',
  'get_lookups',
  'list_decline_reasons',
  'draft_referral_from_text',
  'search_help_center',
] as const

/**
 * Actions that change something. Each one stops at an Approve/Cancel card
 * whose text the server writes from real data, and runs only once approved.
 */
export const ASSISTANT_WRITE_TOOL_NAMES = [
  'submit_proposal',
  'accept_proposal',
  'counter_proposal',
  'decline_proposal',
  'mark_client_contacted',
  'mark_signed',
  'mark_unsigned',
  'share_contact_info',
  'update_case_status',
  'request_case_update',
  'post_referral',
  'repost_referral',
] as const

/** Presentation helpers: no data, no side effects (quick-reply chips). */
export const ASSISTANT_PRESENTATION_TOOL_NAMES = ['suggest_replies'] as const

export type AssistantReadToolName = (typeof ASSISTANT_READ_TOOL_NAMES)[number]
export type AssistantWriteToolName = (typeof ASSISTANT_WRITE_TOOL_NAMES)[number]
export type AssistantPresentationToolName =
  (typeof ASSISTANT_PRESENTATION_TOOL_NAMES)[number]
export type AssistantToolName =
  | AssistantReadToolName
  | AssistantWriteToolName
  | AssistantPresentationToolName

export const isAssistantWriteToolName = (
  name: string
): name is AssistantWriteToolName =>
  (ASSISTANT_WRITE_TOOL_NAMES as readonly string[]).includes(name)

/**
 * Plain-language activity lines, shown while an action runs and after it
 * finishes. Never model output.
 */
export const ASSISTANT_ACTIVITY_LABELS: Record<
  AssistantToolName,
  { running: string; done: string }
> = {
  search_marketplace: {
    running: 'Searching the marketplace…',
    done: 'Searched the marketplace',
  },
  list_my_cases: {
    running: 'Checking your cases…',
    done: 'Checked your cases',
  },
  get_case: { running: 'Opening the case…', done: 'Opened the case' },
  get_needs_attention: {
    running: 'Looking for what needs you…',
    done: 'Checked what needs your attention',
  },
  get_lookups: {
    running: 'Checking the options…',
    done: 'Checked the options',
  },
  list_decline_reasons: {
    running: 'Checking the reasons…',
    done: 'Checked the reasons',
  },
  draft_referral_from_text: {
    running: 'Drafting the referral…',
    done: 'Drafted a referral for you to review',
  },
  search_help_center: {
    running: 'Checking the help center…',
    done: 'Checked the help center',
  },
  submit_proposal: {
    running: 'Sending your proposal…',
    done: 'Sent a proposal',
  },
  accept_proposal: {
    running: 'Accepting the proposal…',
    done: 'Accepted a proposal',
  },
  counter_proposal: {
    running: 'Sending your counteroffer…',
    done: 'Sent a counteroffer',
  },
  decline_proposal: {
    running: 'Declining the proposal…',
    done: 'Declined a proposal',
  },
  mark_client_contacted: {
    running: 'Marking the client contacted…',
    done: 'Marked the client contacted',
  },
  mark_signed: {
    running: 'Marking the client signed…',
    done: 'Marked the client signed',
  },
  mark_unsigned: {
    running: 'Marking the client not signed…',
    done: 'Marked the client not signed',
  },
  share_contact_info: {
    running: 'Sharing the contact info…',
    done: 'Shared the contact info',
  },
  update_case_status: {
    running: 'Posting your status update…',
    done: 'Posted a status update',
  },
  request_case_update: {
    running: 'Requesting a status update…',
    done: 'Requested a status update',
  },
  post_referral: {
    running: 'Posting your referral…',
    done: 'Posted a referral',
  },
  repost_referral: {
    running: 'Reposting the referral…',
    done: 'Reposted a referral',
  },
  suggest_replies: { running: '', done: '' },
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/**
 * Record ids an output exposes. Built from known fields only, never from free
 * text. A change may only target a case or proposal that an earlier result
 * in the conversation listed here.
 */
export interface AssistantRefs {
  caseIds: string[]
  proposalIds: string[]
}

/** A failure the UI can show and the model can explain. */
export interface AssistantToolError {
  ok: false
  code: string
  /** Plain-language explanation, written by the server. */
  message: string
  /** The case it concerns, for an "Open case" link. */
  caseId?: string
}

export const isAssistantToolError = (
  value: unknown
): value is AssistantToolError =>
  !!value &&
  typeof value === 'object' &&
  (value as { ok?: unknown }).ok === false &&
  typeof (value as { message?: unknown }).message === 'string'

/** Which side of a referral the signed-in user is on. */
export type AssistantCaseRole = 'referring' | 'receiving' | 'prospective'

/** A case as a compact card: search hits, list rows, needs-attention items. */
export interface AssistantCaseCard {
  caseId: string
  title: string
  /** Status label as the app shows it, e.g. "In negotiation". */
  status: string
  /** Referral fee (%). Null when unknown. */
  fee: number | null
  practiceArea: string | null
  jurisdiction: string | null
  processStage?: string | null
  postedAt?: string | null
  /** The firm that posted the case (marketplace) or the other side (lists). */
  firm?: string | null
  /** Short, contact-free excerpt of the description. */
  summary?: string | null
  needsAttention?: boolean
  proposalCount?: number
  role?: AssistantCaseRole
}

export interface AssistantProposal {
  proposalId: string
  firm: string | null
  attorney: string | null
  /** The fee (%) currently on the table. */
  fee: number | null
  /** Plain status, e.g. "Proposed", "Counteroffer", "Accepted". */
  status: string
  /** Whose move it is. */
  waitingOn: 'you' | 'them'
  lastActivityAt: string | null
  canAccept: boolean
  canCounter: boolean
  canDecline: boolean
  /** The referring firm invited this attorney rather than them proposing. */
  isInvitation: boolean
  /** Whether the receiving firm has marked the client contacted. */
  clientContacted?: boolean
}

export interface AssistantCaseDetail extends AssistantCaseCard {
  role: AssistantCaseRole
  /** The case page's one-line status, e.g. "Waiting on Smith & Lane." */
  statusLine: string | null
  /** Contact-free description. */
  description: string
  incidentDate: string | null
  proposals: AssistantProposal[]
  /** The changes the app currently offers this user on this case. */
  availableActions: AssistantWriteToolName[]
  clientContact: {
    /** Seller side: the client is marked currently represented. */
    represented: boolean
    /** The contact info is still withheld from the receiving firm. */
    withheld: boolean
    sharedAt: string | null
  }
  /** Post-signed matter stage label, when the case is signed. */
  matterStage: string | null
  /** Match fee already on file for the user's own match, in USD. */
  matchFee: number | null
}

// ---------------------------------------------------------------------------
// Read outputs
// ---------------------------------------------------------------------------

export interface AssistantSearchOutput {
  ok: true
  kind: 'cases'
  query: string
  total: number
  page: number
  totalPages: number
  cases: AssistantCaseCard[]
  refs: AssistantRefs
}

export type AssistantMyCasesSide = 'outbound' | 'inbound'
export type AssistantMyCasesTab =
  | 'counts'
  | 'needs_action'
  | 'open'
  | 'matched'
  | 'signed'
  | 'inactive'

export interface AssistantMyCasesOutput {
  ok: true
  kind: 'my_cases'
  side: AssistantMyCasesSide
  tab: AssistantMyCasesTab
  /** Present for tab "counts". */
  counts?: Record<string, number>
  cases: AssistantCaseCard[]
  page: number
  totalPages: number | null
  refs: AssistantRefs
}

export interface AssistantCaseOutput {
  ok: true
  kind: 'case'
  case: AssistantCaseDetail
  refs: AssistantRefs
}

export interface AssistantNeedsAttentionOutput {
  ok: true
  kind: 'needs_attention'
  outbound: { count: number; cases: AssistantCaseCard[] }
  inbound: { count: number; cases: AssistantCaseCard[] }
  refs: AssistantRefs
}

export interface AssistantLookupOption {
  id: string
  label: string
}

export interface AssistantLookupsOutput {
  ok: true
  kind: 'lookups'
  practiceAreas?: AssistantLookupOption[]
  jurisdictions?: AssistantLookupOption[]
  processStages?: AssistantLookupOption[]
  matterStages?: AssistantLookupOption[]
}

export interface AssistantDeclineReasonsOutput {
  ok: true
  kind: 'decline_reasons'
  reasons: Array<{
    declineReasonId: string
    label: string
    /** Declining for this reason puts the case back on the marketplace. */
    relistsCase: boolean
  }>
}

/** A referral ready to post, built from the user's own text. */
export interface AssistantReferralDraft {
  title: string
  description: string
  practiceAreaId: string | null
  practiceArea: string | null
  jurisdictionId: string | null
  jurisdiction: string | null
  processStageId: string | null
  processStage: string | null
  /** YYYY-MM-DD */
  incidentDate: string | null
  fee: number
  client: {
    firstName: string
    lastName: string
    phone: string | null
    email: string | null
  }
}

export interface AssistantReferralDraftOutput {
  ok: true
  kind: 'referral_draft'
  draft: AssistantReferralDraft
  /** Plain-language list of what the user still has to supply. */
  missing: string[]
}

/**
 * Where help-center links may point. The card renders a link only when its
 * origin is listed here (history comes back from the browser, so a link is
 * never trusted as is).
 */
export const ASSISTANT_HELP_CENTER_ORIGINS = [
  'https://243216470.hs-sites-na2.com',
] as const

/** The help center's own search page, for "nothing found" (append `?term=`). */
export const ASSISTANT_HELP_CENTER_SEARCH_PATH =
  '/as-help-center/kb-search-results'

/** One help-center article section, cited by title and link. */
export interface AssistantHelpArticle {
  title: string
  /** The section heading within the article, when it has one. */
  section: string | null
  category: string
  /** https link to the article, on an ASSISTANT_HELP_CENTER_ORIGINS host. */
  url: string
  /** Plain-text excerpt for the model; the card doesn't show it. */
  excerpt: string
  /** YYYY-MM-DD the article was last updated. */
  updated: string
}

export interface AssistantHelpArticlesOutput {
  ok: true
  kind: 'help_articles'
  query: string
  /** At most three; empty when nothing in the help center matched. */
  articles: AssistantHelpArticle[]
  /** The turn's search limit was reached; the card renders nothing. */
  limited?: true
  /** For the model only, with `limited`. */
  note?: string
}

export interface AssistantSuggestRepliesOutput {
  ok: true
  kind: 'suggest_replies'
  question: string | null
  options: string[]
}

// ---------------------------------------------------------------------------
// Write outputs and approval cards
// ---------------------------------------------------------------------------

/** What a completed change returns. All text is written by the server. */
export interface AssistantActionOutput {
  ok: true
  kind: 'action'
  tool: AssistantWriteToolName
  /** One sentence: what happened. */
  headline: string
  /** Optional follow-up detail (e.g. "Matching finishes in the background"). */
  detail?: string
  caseId?: string
  /** A case the change created (post or repost). */
  newCaseId?: string
  /**
   * Link to this My Cases section instead of a case: a referral submitted
   * for scoring has no case yet.
   */
  openMyCases?: AssistantMyCasesSide
  refs: AssistantRefs
}

export interface AssistantApprovalRow {
  label: string
  value: string
}

export interface AssistantApprovalCheckbox {
  id: string
  /** The statement, verbatim from the matching GUI checkbox. */
  label: string
  /** Render links to the Terms and Conditions and the Privacy Policy. */
  termsLinks?: boolean
}

/**
 * Checkbox statements copied verbatim from the GUI so the assistant asks for
 * exactly what the app asks for. Sources:
 * - proposalCertification: Sidebar/CertificationCheckboxLabel.tsx
 * - referralConfidentiality: CaseForm/ConfidentialityCheckbox.tsx
 * - referralCertification: CaseForm/components/ExtraInformation.tsx
 * (The share-contact statement comes from WITHHOLD_CLIENT_CONTACT_COPY.)
 */
export const ASSISTANT_LEGAL_COPY = {
  proposalCertification:
    "Attorney Share is a platform for attorneys only and does not certify users or advise on professional or ethical rules. I am responsible for vetting any lawyer I work with and complying with my jurisdiction's rules on advertising, co-counsel relationships, fee sharing, and client representation. I understand the match fee is owed when I sign a matched client, whether finalized on or off the platform, and that signing off-platform to avoid the fee violates the Terms. By checking this box I agree to the Terms and Conditions and Privacy Policy.",
  referralConfidentiality:
    'I have not included privileged or confidential information in the public case description',
  referralCertification:
    "Attorney Share does not vet or certify the lawyers to whom you may refer a case. I understand that it is my duty to evaluate any lawyer to whom I refer a matter. I further understand that it is solely my duty to ensure compliance with my jurisdiction's Rules of Professional Conduct and other ethical standards in advertising, making a referral, entering into a referral or co-counsel relationship, engaging in fee sharing, and conducting the representation. Attorney Share is a platform that facilitates attorneys connecting with other attorneys and does not advise or provide guidance on such issues. This is not a client to attorney referral service. By checking this box, I certify and warrant that I understand the above and will adhere to my duties accordingly. I agree to the Terms and Conditions outlined in our Privacy Policy.",
} as const

/**
 * The Approve/Cancel card for a change. Built by the server from data it
 * just loaded with the user's own session, never from model text. It travels
 * as JSON in the approval request's `requestReason`; read it with
 * `decodeApprovalCard`.
 */
export interface AssistantApprovalCard {
  version: 1
  tool: AssistantWriteToolName
  /** Heading, e.g. "Accept this proposal?" */
  title: string
  /** One plain sentence describing exactly what happens. */
  summary: string
  rows: AssistantApprovalRow[]
  /** Free text the user wrote that goes to another firm, shown escaped. */
  userText?: { label: string; text: string }
  /** The exact charge, when the change charges a card. */
  charge?: { amountUsd: number; text: string }
  /** Extra plain-language notes ("This can't be undone."). */
  notes: string[]
  /** Statements the user must tick before Approve unlocks (all of them). */
  checkboxes: AssistantApprovalCheckbox[]
  /** For post_referral: the referral exactly as it will be posted. */
  referralPreview?: AssistantReferralDraft
  caseId?: string
  /** Approve button label, e.g. "Accept proposal". */
  confirmLabel: string
  irreversible: boolean
  /** When the server wrote the card (ISO). Approvals expire. */
  issuedAt: string
  /**
   * The facts the user approved. Re-read before the change runs; any
   * difference stops it with CHANGED_SINCE_APPROVAL.
   */
  facts: Record<string, string | number | boolean | null>
}

/** How long an approval card stays valid. */
export const ASSISTANT_APPROVAL_TTL_MS = 15 * 60 * 1000

export const encodeApprovalCard = (card: AssistantApprovalCard): string =>
  JSON.stringify(card)

export const decodeApprovalCard = (
  reason: string | null | undefined
): AssistantApprovalCard | null => {
  if (!reason) return null
  try {
    const parsed = JSON.parse(reason) as Partial<AssistantApprovalCard> | null
    if (
      !parsed ||
      parsed.version !== 1 ||
      typeof parsed.tool !== 'string' ||
      !isAssistantWriteToolName(parsed.tool) ||
      typeof parsed.title !== 'string' ||
      typeof parsed.summary !== 'string' ||
      !Array.isArray(parsed.rows) ||
      !Array.isArray(parsed.notes) ||
      !Array.isArray(parsed.checkboxes) ||
      typeof parsed.confirmLabel !== 'string' ||
      typeof parsed.issuedAt !== 'string' ||
      !parsed.facts ||
      typeof parsed.facts !== 'object'
    ) {
      return null
    }
    return parsed as AssistantApprovalCard
  } catch {
    return null
  }
}

/** Error codes a change can end with, besides the backend's own. */
export const ASSISTANT_ACTION_ERROR_CODES = {
  /** The facts changed between the card and the approval. */
  CHANGED_SINCE_APPROVAL: 'CHANGED_SINCE_APPROVAL',
  /** The app no longer offers this change on the case. */
  NO_LONGER_AVAILABLE: 'NO_LONGER_AVAILABLE',
  /** The card is older than ASSISTANT_APPROVAL_TTL_MS. */
  APPROVAL_EXPIRED: 'APPROVAL_EXPIRED',
  /** No server-written card was found for this approval. */
  APPROVAL_MISSING: 'APPROVAL_MISSING',
  /** The request timed out; the change may or may not have happened. */
  OUTCOME_UNKNOWN: 'OUTCOME_UNKNOWN',
} as const

// ---------------------------------------------------------------------------
// Outputs per action, for typing `tool-<name>` UI message parts
// ---------------------------------------------------------------------------

export interface AssistantToolOutputs {
  search_marketplace: AssistantSearchOutput | AssistantToolError
  list_my_cases: AssistantMyCasesOutput | AssistantToolError
  get_case: AssistantCaseOutput | AssistantToolError
  get_needs_attention: AssistantNeedsAttentionOutput | AssistantToolError
  get_lookups: AssistantLookupsOutput | AssistantToolError
  list_decline_reasons: AssistantDeclineReasonsOutput | AssistantToolError
  draft_referral_from_text: AssistantReferralDraftOutput | AssistantToolError
  search_help_center: AssistantHelpArticlesOutput
  submit_proposal: AssistantActionOutput | AssistantToolError
  accept_proposal: AssistantActionOutput | AssistantToolError
  counter_proposal: AssistantActionOutput | AssistantToolError
  decline_proposal: AssistantActionOutput | AssistantToolError
  mark_client_contacted: AssistantActionOutput | AssistantToolError
  mark_signed: AssistantActionOutput | AssistantToolError
  mark_unsigned: AssistantActionOutput | AssistantToolError
  share_contact_info: AssistantActionOutput | AssistantToolError
  update_case_status: AssistantActionOutput | AssistantToolError
  request_case_update: AssistantActionOutput | AssistantToolError
  post_referral: AssistantActionOutput | AssistantToolError
  repost_referral: AssistantActionOutput | AssistantToolError
  suggest_replies: AssistantSuggestRepliesOutput
}

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

/** Where the user is in the app when they ask. */
export interface AssistantPageContext {
  /** The case page the user has open, if any. */
  caseId?: string
  /**
   * The case the latest user message is about, when the user picked a task
   * suggestion or briefing follow-up (from that message's metadata). Lets
   * the question name the case by title and never show its id.
   */
  focusCaseId?: string
  /** IANA time zone from the browser, for phrasing dates. */
  timeZone?: string
}

/**
 * The chat request body. `messages` are AI SDK UI messages; the history
 * lives in the browser only (nothing is stored server-side).
 */
export interface AssistantChatRequestBody {
  messages: unknown[]
  context?: AssistantPageContext
}

/** HTTP error bodies from the chat route. */
export interface AssistantChatErrorBody {
  error: string
  code:
    | 'UNAUTHORIZED'
    | 'ASSISTANT_UNAVAILABLE'
    | 'INVALID_REQUEST'
    /** 413: the chat is over the size the route sends to the model; Clear it. */
    | 'CONVERSATION_TOO_LONG'
    | 'METHOD_NOT_ALLOWED'
    | 'INTERNAL_ERROR'
    | AssistantLimitCode
  /** Limit errors only: when the limit that was hit resets (ISO, UTC). */
  resetAt?: string
  /** Limit errors only: which limit was hit. */
  scope?: AssistantLimitScope
}

/**
 * 429s from the chat route (ENG-2099). The user's own turn caps give
 * ASSISTANT_RATE_LIMITED; the firm's daily budget or the global ceiling give
 * ASSISTANT_BUDGET_EXHAUSTED. Retrying before `resetAt` gets another 429,
 * except `user_concurrent` (answers already in progress), which clears as
 * soon as one finishes.
 */
export const ASSISTANT_LIMIT_CODES = [
  'ASSISTANT_RATE_LIMITED',
  'ASSISTANT_BUDGET_EXHAUSTED',
] as const

export type AssistantLimitCode = (typeof ASSISTANT_LIMIT_CODES)[number]

export type AssistantLimitScope =
  | 'user_concurrent'
  | 'user_hour'
  | 'user_day'
  | 'org_day'
  | 'global_day'
